import React, { Fragment } from 'react';
import { isObject, map, omit } from 'lodash';
import PropTypes from 'prop-types';

import { classNames, wordify, singularize } from 'utils';

import Icon from 'components/icon';
import Fieldgroup from 'components/form/fieldgroup';
import { MoveDownBounce } from 'components/transitions';

import './flash.scss';

/**
 * A flash body. `{ title, description }` renders the two-line form; anything
 * else is rendered as-is, the way every message used to be.
 */
function FlashBody({ message }) {
  if (isObject(message) && (message.title || message.description)) {
    return (
      <Fragment>
        <div className="flash-message-title">{message.title}</div>
        {message.description && (
          <div className="flash-message-description">{message.description}</div>
        )}
      </Fragment>
    );
  }

  return message;
}

FlashBody.propTypes = {
  message: PropTypes.any,
};

/**
 * A message may carry its own `className`, so one message can be styled
 * without changing how every flash of that kind looks.
 * @param {string} kind
 * @param {*} message
 * @returns {string}
 */
function flashClassName(kind, message) {
  return classNames('flash-message', kind, {
    [message?.className]: isObject(message) && Boolean(message.className),
  });
}

export default class Flash extends React.Component {
  static contextTypes = {
    clearFlash: PropTypes.func.isRequired,
    cancelAutoFlash: PropTypes.func.isRequired,
  };

  renderError(error) {
    if (error instanceof Error) {
      return error.message;
    }

    if (isObject(error)) {
      const { message } = error;
      const errors = error.errors || error;

      return (
        <Fragment>
          {message || ''}
          {errors && (
            <ul className={error.message ? 'with-message' : undefined}>
              {map(errors, (fieldError, key) => (
                <li key={key}>{this.renderFieldError(key, fieldError)}</li>
              ))}
            </ul>
          )}
        </Fragment>
      );
    }

    return error;
  }

  renderFieldError(path, error) {
    switch (error && error.code) {
      case 'REQUIRED':
        return this.renderFieldErrorDetail(
          `${this.wordifyPath(path)} required`,
          path,
          error,
        );
      case 'UNIQUE':
        return this.renderFieldErrorDetail(
          `${this.wordifyPath(path)} already exists`,
          path,
          error,
        );
      case 'INVALID':
        return this.renderFieldErrorDetail(error.message, path, error);
      default:
        return (
          error &&
          this.renderFieldErrorDetail(
            `Field error: ${error.message}`,
            path,
            error,
          )
        );
    }
  }

  wordifyPath(path) {
    return wordify(path)
      .split(' ')
      .map((word) => singularize(word).replace(/^id$/i, 'ID'))
      .join(' ');
  }

  renderFieldErrorDetail(message, path, error) {
    const params = { field: path, ...omit(error, ['code', 'message']) };
    return (
      <Fragment>
        <Fieldgroup label={message} className="flash-error-details">
          <code>
            <pre>{JSON.stringify(params, null, 2)}</pre>
          </code>
        </Fieldgroup>
      </Fragment>
    );
  }

  render() {
    const { error, info, warning, success, visible } = this.props;

    const { clearFlash } = this.context;
    const { cancelAutoFlash } = this.context;

    return (
      <div
        className={`flash-container ${success ? 'success' : ''} ${
          warning ? 'warning' : ''
        }`}
      >
        {error && (
          <MoveDownBounce active={visible} distance={215}>
            <div
              className={flashClassName('flash-error', error)}
              onClick={cancelAutoFlash}
            >
              <Icon fa="exclamation-circle" className="flash-message-icon" />

              {this.renderError(error)}

              <button
                className="flash-clear"
                onClick={clearFlash}
                type="button"
              >
                <Icon fa="times" faType="light" />
              </button>
            </div>
          </MoveDownBounce>
        )}

        {info && (
          <MoveDownBounce active={visible} distance={215}>
            <div
              className={flashClassName('flash-info', info)}
              onClick={cancelAutoFlash}
            >
              <Icon fa="info-circle" className="flash-message-icon" />

              <FlashBody message={info} />

              <button
                className="flash-clear"
                onClick={clearFlash}
                type="button"
              >
                <Icon fa="times" faType="light" />
              </button>
            </div>
          </MoveDownBounce>
        )}

        {warning && (
          <MoveDownBounce active={visible} distance={215}>
            <div
              className={flashClassName('flash-warning', warning)}
              onClick={cancelAutoFlash}
            >
              <Icon fa="exclamation-triangle" className="flash-message-icon" />

              <FlashBody message={warning} />

              <button
                className="flash-clear"
                onClick={clearFlash}
                type="button"
              >
                <Icon fa="times" faType="light" />
              </button>
            </div>
          </MoveDownBounce>
        )}

        {success && (
          <MoveDownBounce active={visible} distance={215}>
            <div
              className={flashClassName('flash-success', success)}
              onClick={cancelAutoFlash}
            >
              <Icon fa="check" className="flash-message-icon" />

              <FlashBody message={success} />

              <button
                className="flash-clear"
                onClick={clearFlash}
                type="button"
              >
                <Icon fa="times" faType="light" />
              </button>
            </div>
          </MoveDownBounce>
        )}
      </div>
    );
  }
}
